import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import api from '../utils/api'
import { formatDateTime } from '../utils/dateFormatter'
import './QRCodeDetails.css'

const QRCodeDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [qrcode, setQrcode] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [qrImage, setQrImage] = useState(null)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    is_active: true,
    is_car: true
  })
  const [linkingData, setLinkingData] = useState({
    first_name: '',
    last_name: '',
    vehicle_number: '',
    color: '',
    brand_or_details: '',
    mobile: '',
    emergency_number: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (id) {
      fetchQRCode()
    }
  }, [id])

  useEffect(() => {
    if (qrcode) {
      generateQRImage()
      setFormData({
        is_active: qrcode.is_active,
        is_car: qrcode.is_car
      })
      if (qrcode.linking) {
        setLinkingData({
          first_name: qrcode.linking.first_name || '',
          last_name: qrcode.linking.last_name || '',
          vehicle_number: qrcode.linking.vehicle_number || '',
          color: qrcode.linking.color || '',
          brand_or_details: qrcode.linking.brand_or_details || '',
          mobile: qrcode.linking.mobile || '',
          emergency_number: qrcode.linking.emergency_number || ''
        })
      }
    }
  }, [qrcode])

  const fetchQRCode = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await api.get(`/qrcodes/${id}`)
      if (response.data.success) {
        setQrcode(response.data.data.qrcode)
      }
    } catch (error) {
      console.error('Error fetching QR code:', error)
      setError(error.response?.data?.message || 'Error loading QR code')
    } finally {
      setLoading(false)
    }
  }

  const generateQRImage = async () => {
    if (!qrcode) return
    try {
      const qrUrl = `${window.location.origin}/qr/${qrcode.code_uuid}`
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      setQrImage(qrDataUrl)
    } catch (error) {
      console.error('Error generating QR image:', error)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const response = await api.put(`/qrcodes/${id}`, formData)
      if (response.data.success) {
        alert('QR code updated successfully!')
        fetchQRCode()
        setEditing(false)
      }
    } catch (error) {
      console.error('Error updating QR code:', error)
      alert(error.response?.data?.message || 'Error updating QR code')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLinkUpdate = async (e) => {
    e.preventDefault()
    // Front-end validations
    const hasName = (linkingData.first_name || '').trim() || (linkingData.last_name || '').trim()
    if (!hasName) {
      alert('First name or Last name is required')
      return
    }

    if (linkingData.mobile && !/^[0-9]{10}$/.test(linkingData.mobile)) {
      alert('Owner mobile number must be exactly 10 digits')
      return
    }

    if (linkingData.emergency_number && !/^[0-9]{10}$/.test(linkingData.emergency_number)) {
      alert('Emergency mobile number must be exactly 10 digits')
      return
    }

    setSubmitting(true)

    try {
      const response = await api.post(`/qrcodes/${id}/link`, linkingData)
      if (response.data.success) {
        alert('Vehicle details updated successfully!')
        fetchQRCode()
      }
    } catch (error) {
      console.error('Error updating linking:', error)
      alert(error.response?.data?.message || 'Error updating vehicle details')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this QR code? This action cannot be undone.')) {
      return
    }

    try {
      const response = await api.delete(`/qrcodes/${id}`)
      if (response.data.success) {
        alert('QR code deleted successfully!')
        navigate('/admin/qrcodes')
      }
    } catch (error) {
      console.error('Error deleting QR code:', error)
      alert(error.response?.data?.message || 'Error deleting QR code')
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
    })
  }

  const handleLinkingChange = (e) => {
    let { name, value } = e.target;
    if (name === 'vehicle_number') {
      value = value.toUpperCase();
    }
    if (name === 'mobile' || name === 'emergency_number') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setLinkingData({
      ...linkingData,
      [name]: value
    })
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading QR code details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div className="alert alert-error">{error}</div>
          <button className="btn-primary" onClick={() => navigate('/admin/qrcodes')}>
            Back to QR Codes
          </button>
        </div>
      </div>
    )
  }

  if (!qrcode) {
    return null
  }

  return (
    <div className="qrcode-details-page animate-fadeIn">
      <div className="details-header">
        <div className="details-header-info">
          <button
            className="btn-secondary"
            onClick={() => navigate('/admin/qrcodes')}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', minHeight: 'unset' }}
          >
            ← Back to Tags
          </button>
          <h1>QR Code Details</h1>
          <p className="details-subtitle">Manage vehicle tracking & identification</p>
        </div>
        <button
          className="btn-pill btn-pill-secondary"
          onClick={() => setEditing(!editing)}
        >
          {editing ? '✕ Cancel' : '✎ Edit Status'}
        </button>
      </div>

      <div className="details-grid">
        {/* Left Column: QR Preview */}
        <div className="card qr-preview-card">
          <div className="qr-image-container">
            {qrImage ? (
              <img src={qrImage} alt="QR Code" />
            ) : (
              <div className="loading-spinner-small"></div>
            )}
          </div>

          <div className="uuid-box">
            <span className="uuid-label">UNIQUE IDENTITY (UUID)</span>
            <div className="uuid-value">{qrcode.code_uuid}</div>
          </div>

          <div style={{ marginTop: '1.25rem', width: '100%' }}>
            <a
              href={`${window.location.origin}/qr/${qrcode.code_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-pill-primary"
              style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}
            >
              🌐 View Public Profile
            </a>
          </div>
        </div>

        {/* Right Column: QR Info / Edit Form */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Technical Configuration</h2>
          </div>

          {editing ? (
            <form onSubmit={handleUpdate} className="info-list">
              <div className="form-group">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  Status: <strong>{formData.is_active ? 'ENABLED' : 'DISABLED'}</strong>
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    name="is_car"
                    checked={formData.is_car}
                    onChange={handleChange}
                  />
                  Vehicle Class: <strong>{formData.is_car ? 'CAR / SUV' : 'BIKE / SCOOTER'}</strong>
                </label>
              </div>
              <button
                type="submit"
                className="btn-primary"
                style={{ marginTop: '1rem', width: '100%' }}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Save Configuration'}
              </button>
            </form>
          ) : (
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">Current Status</span>
                <span className={`badge ${qrcode.is_active ? 'badge-success' : 'badge-error'}`}>
                  {qrcode.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Vehicle Type</span>
                <span className={`badge ${qrcode.is_car ? 'badge-info' : 'badge-warning'}`}>
                  {qrcode.is_car ? '🚗 Car / SUV' : '🏍️ Bike / Scooter'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Generated On</span>
                <span className="info-value">{formatDateTime(qrcode.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Internal Reference</span>
                <span className="info-value"># {qrcode.id}</span>
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Information Section */}
        <div className="card full-width-section">
          <div className="card-header">
            <h2 className="card-title">Linked Vehicle Profile</h2>
          </div>

          {!qrcode.linking && (
            <div className="no-data-alert">
              <span>⚠️</span>
              <strong>No vehicle linked yet.</strong> Details will appear once scanned or manually added.
            </div>
          )}

          <form onSubmit={handleLinkUpdate}>
            <div className="vehicle-form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={linkingData.first_name}
                  onChange={handleLinkingChange}
                  placeholder="Owner first name"
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={linkingData.last_name}
                  onChange={handleLinkingChange}
                  placeholder="Owner last name"
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label>Plate Number</label>
                <input
                  type="text"
                  name="vehicle_number"
                  value={linkingData.vehicle_number}
                  onChange={handleLinkingChange}
                  placeholder="e.g. DL 01 AB 1234"
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label>Exterior Color</label>
                <input
                  type="text"
                  name="color"
                  value={linkingData.color}
                  onChange={handleLinkingChange}
                  placeholder="e.g. Midnight Black"
                  disabled={submitting}
                />
              </div>
              <div className="form-group span-2">
                <label>Brand & Model Specs</label>
                <textarea
                  name="brand_or_details"
                  value={linkingData.brand_or_details}
                  onChange={handleLinkingChange}
                  placeholder="Additional details about the vehicle..."
                  rows="2"
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label>Owner Contact</label>
                <div className="mobile-input-wrapper-details" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                  <span style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)', fontWeight: 600 }}>+91</span>
                  <input
                    type="tel"
                    name="mobile"
                    value={linkingData.mobile}
                    onChange={handleLinkingChange}
                    placeholder="9876543210"
                    disabled={submitting}
                    maxLength="10"
                    style={{ border: 'none', flex: 1, padding: '0.75rem', outline: 'none' }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Emergency Contact</label>
                <div className="mobile-input-wrapper-details" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                  <span style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)', fontWeight: 600 }}>+91</span>
                  <input
                    type="tel"
                    name="emergency_number"
                    value={linkingData.emergency_number}
                    onChange={handleLinkingChange}
                    placeholder="9876543210"
                    disabled={submitting}
                    maxLength="10"
                    style={{ border: 'none', flex: 1, padding: '0.75rem', outline: 'none' }}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary"
              style={{ marginTop: '1.5rem', width: '100%' }}
              disabled={submitting}
            >
              {submitting ? 'Updating...' : qrcode.linking ? 'Update Profile Details' : 'Initialize Vehicle Profile'}
            </button>
          </form>
        </div>

        {/* Global Actions Area */}
        <div className="card full-width-section">
          <div className="card-header">
            <h2 className="card-title">Administrative Actions</h2>
          </div>
          <div className="actions-pills">
            <button
              className="btn-pill btn-pill-secondary"
              onClick={() => {
                const iconStroke = '#000'
                const iconFill = '#fff'
               const iconParking = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="9" />
  <path d="M9 16V8h4a2.5 2.5 0 010 5H9" />
  <path d="M5 19L19 5" />
</svg>`;
    const iconRegistered = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 006 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
  <path d="M9 18h6" />
  <path d="M10 22h4" />
</svg>`;
const iconWarning = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2">
  <path d="M12 3l10 18H2L12 3z"/>
  <path d="M12 9v4"/>
  <circle cx="12" cy="17" r="1"/>
</svg>`;
const iconPhone = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2">
  <path d="M22 16.9v3a2 2 0 01-2.2 2A19.8 19.8 0 013 4.1 2 2 0 015 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.6a2 2 0 01-.5 2.1L9 9a16 16 0 006 6l1-1a2 2 0 012.1-.5c.8.3 1.7.5 2.6.6a2 2 0 011.7 2z"/>
</svg>`;
                // const iconParking = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 10h2v6H7zM15 10h2v6h-2z"/></svg>'
                // const iconRegistered = '<span class="tag-icon-circle">&#9415;</span>'
                // const iconWarning = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M12 8v4M12 16h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>'
                // const iconPhone = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>'
                const indiaFlagSvg = '<svg class="india-flag-svg" viewBox="0 0 21 14" xmlns="http://www.w3.org/2000/svg"><rect width="21" height="4.67" fill="#FF9933"/><rect y="4.67" width="21" height="4.66" fill="#fff"/><rect y="9.33" width="21" height="4.67" fill="#138808"/><circle cx="10.5" cy="7" r="1.5" fill="#000080"/><circle cx="10.5" cy="7" r="1.1" fill="#fff" opacity="0.95"/></svg>'
                const printWindow = window.open('', '_blank')
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>NAH44 - QR ID ${qrcode.id}</title>
                      <style>
                        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap');
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { font-family: 'Outfit', sans-serif; background: #f0f0f0; padding: 20px; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        .sheet { display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                        .tag-card { width: 4in; height: 2.5in; display: flex; overflow: hidden; position: relative; border: 1px solid #ccc; box-shadow: 0 2px 12px rgba(0,0,0,0.15); }
                        .left-panel { width: 42%; background: #FF8C00 !important; padding: 6px 6px; display: flex; flex-direction: column; align-items: center; justify-content: space-between; -webkit-print-color-adjust: exact !important; border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
                        .white-qr-panel { width: 100%; background: white !important; border: 3px solid #FF8C00 !important; border-radius: 8px; padding: 3px; display: flex; flex-direction: column; align-items: center; -webkit-print-color-adjust: exact !important; }
                        .qr-wrapper { width: 92%; aspect-ratio: 1; display: flex; align-items: center; justify-content: center; border: none; padding: 0; border-radius: 4px; overflow: hidden; }
                        .qr-wrapper img { width: 100%; height: 100%; display: block; border-radius: 4px; }
                        .tag-branding-mini { display: flex; align-items: center; gap: 8px; justify-content: space-between; width: 100%; margin-top: 1px; padding: 0 2px; }
                        .mini-logo-box { border: 1px solid #E89B2C !important; border-radius: 4px; padding: 2px 5px; background: rgba(255,250,240,0.95) !important; display: inline-flex; align-items: center; gap: 0; -webkit-print-color-adjust: exact !important; }
                        .mini-logo-nah { color: #FF8C00 !important; font-weight: 900; font-size: 8px; text-shadow: 0 0 1px rgba(0,0,0,0.2); }
                        .mini-logo-44 { color: #00458B !important; font-weight: 900; font-size: 8px; text-shadow: 0 0 1px rgba(0,0,0,0.2); }
                        .india-flag-below { display: inline-flex; align-items: center; justify-content: center; }
                        .india-flag-svg { width: 16px; height: auto; display: block; border: 0.5px solid rgba(0,0,0,0.12); border-radius: 1px; flex-shrink: 0; }
                        .icons-row { display: flex; justify-content: center; align-items: center; gap: 5px; margin: 1px 0 0 0; }
                        .icons-row .tag-icon { width: 12px; height: 12px; display: block; }
                        .icons-row .tag-icon-circle { font-size: 10px !important; display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border: 1.5px solid #000; border-radius: 50%; background: #fff !important; color: #000 !important; font-weight: bold; -webkit-print-color-adjust: exact !important; }
                        .bottom-text-left { font-size: 6px; text-align: center; line-height: 1.4; font-weight: 700; color: #000 !important; letter-spacing: 0.02em; padding: 0 4px; -webkit-print-color-adjust: exact !important; }
                        .bottom-text-left .line1 { font-size: 1.05em; font-weight: 800; }
                        .right-panel { width: 58%; background: white !important; padding: 14px 16px 14px 14px; display: flex; flex-direction: column; justify-content: flex-start; position: relative; border-top-right-radius: 12px; border-bottom-right-radius: 12px; overflow: hidden; }
                        .right-panel::before { content: ''; position: absolute; top: 0; right: 0; width: 0; height: 0; border-style: solid; border-width: 0 35px 35px 0; border-color: transparent #FF8C00 transparent transparent; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        .right-header { margin-bottom: 10px; }
                        .nah-brand-framed { border: 3px solid #E89B2C !important; border-radius: 4px; padding: 6px 14px; display: inline-flex; align-items: center; gap: 2px; font-weight: 900; font-size: 22px; background: linear-gradient(180deg, #f5f0ea 0%, #e8e4df 100%) !important; box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(255,255,255,0.6), 2px 2px 4px rgba(0,0,0,0.2), -1px -1px 2px rgba(255,255,255,0.4); -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        .brand-nah { color: #FF8C00 !important; text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2); }
                        .brand-44 { color: #00458B !important; text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2); }
                        .scan-call-to-action { font-size: 22px; font-weight: 900; color: #1A479F !important; line-height: 1.35; margin: 0 0 4px 0; letter-spacing: 0.02em; text-transform: uppercase; }
                        .scan-call-to-action .highlight { color: #FF8C00 !important; -webkit-print-color-adjust: exact !important; }
                        .scan-call-to-action .underline { text-decoration: none; border-bottom: 2.5px solid #FF8C00; padding-bottom: 1px; display: inline; }
                        .small-instructions { font-size: 7px; color: #000 !important; font-weight: 400; text-transform: uppercase; line-height: 1.5; margin-top: 10px; padding-top: 0; letter-spacing: 0.03em; }
                        @media print { body { background: #fff; padding: 0; } .sheet { min-height: auto; padding: 0.25in; } .tag-card { box-shadow: none; } }
                      </style>
                    </head>
                    <body>
                      <div class="sheet">
                        <div class="tag-card">
                          <div class="left-panel">
                            <div class="white-qr-panel">
                              <div class="qr-wrapper">
                                <img src="${qrImage || ''}" alt="QR" />
                              </div>
                              <div class="tag-branding-mini">
                                <span class="mini-logo-box"><span class="mini-logo-nah">NAH</span><span class="mini-logo-44">44</span></span>
                                <span class="india-flag-below">${indiaFlagSvg}</span>
                              </div>
                            </div>
                            <div class="icons-row">${iconParking} ${iconRegistered} ${iconWarning} ${iconPhone}</div>
                            <div class="bottom-text-left"><span class="line1">Wrong Parking. Emergency Contact.</span><br/>any issue with the vehicle, Scan the QR.</div>
                          </div>
                          <div class="right-panel">
                            <div class="right-header">
                              <div class="nah-brand-framed"><span class="brand-nah">NAH</span><span class="brand-44">44</span></div>
                            </div>
                            <div class="scan-call-to-action">Scan the code<br/>to contact the<br/><span class="underline"><span class="highlight">vehicle</span> owner.</span></div>
                            <div class="small-instructions">SCAN USING PHONE CAMERA, GOOGLE LENS OR ANY QR SCANNER APP,UPI APP SCANNER ,VISIT NAH44. COM FOR MORE.</div>
                          </div>
                        </div>
                      </div>
                    </body>
                  </html>
                `)
                printWindow.document.close()
                setTimeout(() => printWindow.print(), 500)
              }}
            >
              🖨️ Print Batch
            </button>
            <button
              className="btn-pill btn-pill-secondary"
              onClick={() => {
                const qrUrl = `${window.location.origin}/qr/${qrcode.code_uuid}`
                navigator.clipboard.writeText(qrUrl)
                alert('Success: Link copied to clipboard')
              }}
            >
              📋 Copy Link
            </button>
            <button
              className="btn-pill btn-pill-danger"
              style={{ marginLeft: 'auto' }}
              onClick={handleDelete}
            >
              🗑️ Terminate QR Identity
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QRCodeDetails

